import {BottomDrawerCloseIndicator} from '@frontend/components/BottomDrawerCloseIndicator'
import {trans} from '@frontend/helpers'
import type {AdPropertyConfigForFilters} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useAdCategoryFiltersForm} from '@frontend/modules/public/ads/hooks/useAdCategoryFiltersForm'
import {PublicAdsApi} from '@frontend/modules/public/ads/PublicAdsApi'
import {mdiChevronDown} from '@mdi/js'
import {iconMenuSearch} from '@shared/helpers/icons'
import clsx from 'clsx'
import {
    useEffect,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {Modal} from 'swayok-react-mdb-ui-kit/components/Modal/Modal'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalFooter} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooter'
import {ModalHeader} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeader'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {AdCategoryFiltersDynamicInputs} from './AdCategoryFiltersDynamicInputs'
import {CommunicationMethodFilter} from './filters/CommunicationMethodFilter'
import {PriceFilter} from './filters/PriceFilter'

interface Props {
    services?: boolean
    onOpenAllFilters: () => void
    className?: string
}

const priceFilterName: string = 'price'
const communicationMethodFilterName: string = 'communicationMethod'

// Быстрые фильтры на телефонах: каждая кнопка открывает нижнюю панель с одним фильтром.
export function ItemsCategoryQuickFiltersMobile(props: Props) {

    const {
        services,
        onOpenAllFilters,
        className,
    } = props

    const [
        configs,
        setConfigs,
    ] = useState<AdPropertyConfigForFilters[]>([])

    // Имя фильтра остается после закрытия панели, чтобы содержимое не пропадало во время анимации.
    const [
        filterName,
        setFilterName,
    ] = useState<string | null>(null)
    const [
        isOpened,
        setIsOpened,
    ] = useState<boolean>(false)

    const close = () => setIsOpened(false)

    const {
        filters,
        setFilters,
        isSubmitting,
        onSubmit,
        resetFromUrl,
        categoryId,
    } = useAdCategoryFiltersForm(close)

    const isPriceFilter: boolean = filterName === priceFilterName
    const isCommunicationMethodFilter: boolean = filterName === communicationMethodFilterName
    const config = configs.find(
        item => item.name === filterName
    )

    // Все панели делят одно состояние, поэтому непримененные изменения нужно отменить,
    // иначе они применятся вместе со следующим фильтром.
    const cancel = () => {
        resetFromUrl()
        close()
    }

    useEffect(() => {
        if (categoryId) {
            PublicAdsApi.getCategoryAdPropertiesForFilters(categoryId)
                .then(setConfigs)
                .catch(() => {
                    // Игнорируем.
                })
        } else {
            setConfigs([])
        }
    }, [categoryId])

    const open = useEventCallback((name: string) => {
        setFilterName(name)
        setIsOpened(true)
    })

    const reset = useEventCallback(() => {
        if (isCommunicationMethodFilter) {
            setFilters(filters => ({
                ...filters,
                communicationMethod: [],
            }))
        } else if (isPriceFilter) {
            setFilters(filters => ({
                ...filters,
                minPrice: null,
                maxPrice: null,
            }))
        } else if (config) {
            setFilters(filters => ({
                ...filters,
                props: {
                    ...filters.props,
                    [config.name]: undefined,
                },
            }))
        }
    })

    let modalTitle = ''
    if (isCommunicationMethodFilter) {
        modalTitle = trans().ads.list.filters.communication_method
    } else if (isPriceFilter) {
        modalTitle = trans().ads.list.filters.price
    } else if (config) {
        modalTitle = config.props.label
    }

    const renderButton = (name: string, label: string, isActive: boolean) => (
        <Button
            key={name}
            color="light"
            small
            active={isActive}
            className="flex-shrink-0 text-nowrap with-icon-flex gap-1"
            onClick={() => open(name)}
        >
            {label}
            <Icon
                path={mdiChevronDown}
                size={18}
            />
        </Button>
    )

    if (services && configs.length === 0) {
        // Кроме кнопки всех фильтров показать нечего, а она дублирует кнопку над строкой.
        return null
    }

    return (
        <div className="offered-items-quick-filters">
            <div className={clsx('offered-items-quick-filters-scrollable d-flex flex-row gap-2 overflow-auto', className)}>
                <Button
                    color="light"
                    small
                    active
                    className="flex-shrink-0 px-2"
                    title={trans().ads.list.filters.header}
                    onClick={onOpenAllFilters}
                >
                    <Icon path={iconMenuSearch} />
                </Button>
                {renderButton(
                    communicationMethodFilterName,
                    trans().ads.list.filters.communication_method,
                    !!filters.communicationMethod?.length
                )}
                {!services && renderButton(
                    priceFilterName,
                    trans().ads.list.filters.price,
                    !!filters.minPrice || !!filters.maxPrice
                )}
                {configs.map(item => renderButton(
                    item.name,
                    item.props.label,
                    hasValue(filters.props?.[item.name])
                ))}
            </div>
            <Modal
                show={isOpened}
                className="bottom-drawer"
                closeOnEsc
                onClose={cancel}
            >
                <form
                    className="modal-content m-0"
                    onSubmit={onSubmit}
                >
                    <BottomDrawerCloseIndicator onClose={cancel} />
                    <ModalHeader
                        title={modalTitle}
                        border={false}
                        onClose={cancel}
                    >
                        <Button
                            color="gray"
                            outline
                            small
                            className="py-1 px-2 text-nowrap ms-auto"
                            onClick={reset}
                        >
                            {trans().ads.list.filters.reset_filter}
                        </Button>
                    </ModalHeader>
                    <ModalBody className="pb-0">
                        {isCommunicationMethodFilter && (
                            <CommunicationMethodFilter
                                value={filters.communicationMethod ?? []}
                                disabled={isSubmitting}
                                noLabel
                                onChange={value => {
                                    setFilters(filters => ({
                                        ...filters,
                                        communicationMethod: value,
                                    }))
                                }}
                            />
                        )}
                        {isPriceFilter && (
                            <PriceFilter
                                minPrice={filters.minPrice ?? null}
                                maxPrice={filters.maxPrice ?? null}
                                setMinPrice={value => {
                                    setFilters(filters => ({
                                        ...filters,
                                        minPrice: value,
                                    }))
                                }}
                                setMaxPrice={value => {
                                    setFilters(filters => ({
                                        ...filters,
                                        maxPrice: value,
                                    }))
                                }}
                                disabled={isSubmitting}
                                noLabel
                            />
                        )}
                        {config && (
                            <AdCategoryFiltersDynamicInputs
                                configs={[config]}
                                values={filters.props ?? {}}
                                disabled={isSubmitting}
                                noLabels
                                onChange={setter => {
                                    setFilters(filters => ({
                                        ...filters,
                                        props: setter(filters.props ?? {}),
                                    }))
                                }}
                            />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="green"
                            type="submit"
                            className="full-width"
                            disabled={isSubmitting}
                        >
                            {trans().ads.list.filters.apply}
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>
        </div>
    )
}

// Задано ли значение фильтра.
function hasValue(value: unknown): boolean {
    return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
}
